import axios from 'axios';

// Usar URL relativa para funcionar em qualquer ambiente (dev, docker, produção)
const API_BASE_URL = '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  const tenantId = localStorage.getItem('selected_tenant_id');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

export interface ImportedContact {
  id: string;
  name: string;
  phone: string;
  source: 'contact' | 'group' | 'chat' | 'label';
  groupName?: string;
  lastMessageTime?: string;
  labelColor?: string; // Cor da label em hex (ex: #64c4ff)
}

export interface WhatsAppLabel {
  id: string;
  name: string;
  color?: number; // 0-19
  colorHex?: string; // ex: #64c4ff
}

export interface GroupInfo {
  id: string;
  name: string;
  participantsCount: number;
  type?: 'group' | 'community';
}

export const whatsappImportApi = {
  // Buscar contatos salvos
  async getContacts(sessionName: string): Promise<ImportedContact[]> {
    const response = await api.get(`/whatsapp-import/contacts/${sessionName}`);
    return response.data.contacts;
  },

  // Buscar lista de grupos
  async getGroups(sessionName: string): Promise<GroupInfo[]> {
    const response = await api.get(`/whatsapp-import/groups/${sessionName}`);
    return response.data.groups;
  },

  // Buscar lista de comunidades
  async getCommunities(sessionName: string): Promise<GroupInfo[]> {
    const response = await api.get(`/whatsapp-import/communities/${sessionName}`);
    return response.data.groups;
  },

  // Buscar participantes de um grupo
  async getGroupParticipants(sessionName: string, groupId: string, groupName: string): Promise<ImportedContact[]> {
    const response = await api.get(`/whatsapp-import/groups/${sessionName}/${encodeURIComponent(groupId)}/participants`, {
      params: { groupName },
    });
    return response.data.participants;
  },

  // Buscar conversas recentes
  async getChats(sessionName: string): Promise<ImportedContact[]> {
    const response = await api.get(`/whatsapp-import/chats/${sessionName}`);
    return response.data.chats;
  },

  // Importar contatos para o banco de dados
  async importContacts(contacts: ImportedContact[], categoryId?: string) {
    const response = await api.post('/whatsapp-import/import', {
      contacts,
      categoryId,
    });
    return response.data;
  },

  // Buscar labels (etiquetas) do WhatsApp Business
  async getLabels(sessionName: string): Promise<WhatsAppLabel[]> {
    const response = await api.get(`/whatsapp-import/labels/${sessionName}`);
    return response.data.labels || [];
  },

  // Buscar contatos por label (etiqueta) do WhatsApp Business
  async getContactsByLabel(sessionName: string, labelId: string, labelName?: string): Promise<ImportedContact[]> {
    const response = await api.get(`/whatsapp-import/labels/${sessionName}/${labelId}/contacts`, {
      params: { labelName },
    });
    return response.data.contacts || [];
  },
};
